/**
 * Card Component
 * ==============
 * Reusable card component with various styles and configurations.
 */

import React from 'react';
import { ArrowUpRight } from 'lucide-react';

/**
 * Card Component
 * Basic card container with padding and styling options.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} [props.variant='default'] - Card variant
 * @param {boolean} [props.hoverable=false] - Enable hover effects
 * @param {boolean} [props.clickable=false] - Show pointer cursor
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Card component
 */
export function Card({
  children,
  variant = 'default',
  hoverable = false,
  clickable = false,
  className = '',
  ...props
}) {
  const variants = {
    default: 'bg-white border border-dme-gray-200',
    elevated: 'bg-white shadow-lg',
    outlined: 'bg-transparent border-2 border-dme-gray-200',
    filled: 'bg-dme-blue-50',
    dark: 'bg-dme-blue-800 text-white',
    gradient: 'bg-gradient-to-br from-dme-blue to-dme-blue-dark text-white',
  };
  
  const classes = `
    rounded-xl overflow-hidden
    ${variants[variant] || variants.default}
    ${hoverable ? 'hover:shadow-xl hover:-translate-y-1 transition-all duration-300' : ''}
    ${clickable ? 'cursor-pointer' : ''}
    ${className}
  `;
  
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
}

/**
 * FeatureCard Component
 * Card designed for feature highlights with icon, title, and description.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.icon - Feature icon
 * @param {string} props.title - Feature title
 * @param {string} props.description - Feature description
 * @param {string} [props.color='blue'] - Accent color
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} FeatureCard component
 */
export function FeatureCard({
  icon,
  title,
  description,
  color = 'blue',
  className = '',
}) {
  const colorClasses = {
    blue: 'bg-dme-blue-50 text-dme-blue',
    orange: 'bg-dme-orange-50 text-dme-orange',
    green: 'bg-dme-green-50 text-dme-green',
  };
  
  return (
    <Card 
      variant="elevated" 
      hoverable 
      className={`p-6 lg:p-8 ${className}`}
    >
      {/* Icon */}
      <div className={`w-14 h-14 rounded-xl ${colorClasses[color] || colorClasses.blue} flex items-center justify-center mb-6`}>
        {icon}
      </div>
      
      {/* Content */}
      <h3 className="font-heading font-bold text-xl text-dme-blue-800 mb-3">
        {title}
      </h3>
      <p className="text-dme-gray-600 leading-relaxed">
        {description}
      </p>
    </Card>
  );
}

/**
 * StatCard Component
 * Card for displaying statistics with large numbers.
 * 
 * @param {object} props - Component props
 * @param {string|number} props.value - Stat value
 * @param {string} [props.suffix] - Value suffix (%, +, etc.)
 * @param {string} props.label - Stat label
 * @param {string} [props.description] - Optional description
 * @param {string} [props.trend] - Trend direction (up/down)
 * @param {string} [props.trendValue] - Trend percentage
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} StatCard component
 */
export function StatCard({
  value,
  suffix = '',
  label,
  description,
  trend,
  trendValue,
  className = '',
}) {
  const trendColors = {
    up: 'text-dme-green',
    down: 'text-red-500',
  };
  
  return (
    <Card 
      variant="elevated" 
      hoverable 
      className={`p-6 lg:p-8 text-center ${className}`}
    >
      {/* Value */}
      <div className="mb-2">
        <span className="font-heading font-extrabold text-4xl lg:text-5xl text-dme-blue-800">
          {value}
        </span>
        {suffix && (
          <span className="font-heading font-bold text-2xl lg:text-3xl text-dme-orange">
            {suffix}
          </span>
        )}
      </div>
      
      {/* Label */}
      <h4 className="font-semibold text-lg text-dme-gray-700 mb-1">
        {label}
      </h4>
      
      {/* Description */}
      {description && (
        <p className="text-sm text-dme-gray-500">
          {description}
        </p>
      )}
      
      {/* Trend */}
      {trend && trendValue && (
        <div className={`mt-3 flex items-center justify-center gap-1 text-sm font-medium ${trendColors[trend]}`}>
          <span>{trend === 'up' ? '↑' : '↓'}</span>
          <span>{trendValue}</span>
        </div>
      )}
    </Card>
  );
}

/**
 * TestimonialCard Component
 * Card for displaying customer testimonials.
 * 
 * @param {object} props - Component props
 * @param {string} props.quote - Testimonial quote
 * @param {string} props.author - Author name
 * @param {string} props.role - Author role
 * @param {string} props.company - Author company
 * @param {string} [props.avatar] - Author avatar URL
 * @param {number} [props.rating] - Star rating (1-5)
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} TestimonialCard component
 */
export function TestimonialCard({
  quote,
  author,
  role,
  company,
  avatar,
  rating,
  className = '',
}) {
  return (
    <Card 
      variant="elevated" 
      className={`p-6 lg:p-8 ${className}`}
    >
      {/* Rating */}
      {rating && (
        <div className="flex gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <svg
              key={i}
              className={`w-5 h-5 ${i < rating ? 'text-dme-orange' : 'text-dme-gray-300'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
      )}
      
      {/* Quote */}
      <blockquote className="text-dme-gray-700 text-lg leading-relaxed mb-6">
        "{quote}"
      </blockquote>
      
      {/* Author */}
      <div className="flex items-center gap-4">
        {avatar ? (
          <img 
            src={avatar} 
            alt={author}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-dme-blue-100 flex items-center justify-center">
            <span className="font-semibold text-dme-blue">
              {author.charAt(0)}
            </span>
          </div>
        )}
        <div>
          <div className="font-semibold text-dme-blue-800">{author}</div>
          <div className="text-sm text-dme-gray-500">
            {role}, {company}
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * IntegrationCard Component
 * Card for displaying integration partners.
 * 
 * @param {object} props - Component props
 * @param {string} props.name - Partner name
 * @param {string} props.category - Partner category
 * @param {string} [props.logo] - Partner logo URL
 * @param {string} [props.status='available'] - Integration status
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} IntegrationCard component
 */
export function IntegrationCard({
  name,
  category,
  logo,
  status = 'available',
  className = '',
}) {
  const statusLabels = {
    available: 'Available',
    'coming-soon': 'Coming Soon',
    beta: 'Beta',
  };
  
  const statusColors = {
    available: 'bg-dme-green text-white',
    'coming-soon': 'bg-dme-gray-200 text-dme-gray-600',
    beta: 'bg-dme-orange text-white',
  };
  
  return (
    <Card 
      variant="outlined" 
      hoverable 
      className={`p-5 ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {/* Logo */}
          {logo ? (
            <img 
              src={logo} 
              alt={name}
              className="w-12 h-12 object-contain"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-dme-blue-100 flex items-center justify-center">
              <span className="font-bold text-dme-blue">
                {name.charAt(0)}
              </span>
            </div>
          )}
          
          {/* Info */}
          <div>
            <h4 className="font-semibold text-dme-blue-800">{name}</h4>
            <p className="text-sm text-dme-gray-500">{category}</p>
          </div>
        </div>
        
        {/* Status Badge */}
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
          {statusLabels[status]}
        </span>
      </div>
    </Card>
  );
}

export default Card;
